import React, { useEffect, useState } from "react";
import { hayDialogoInstalacion, onCambioInstalacion, lanzarInstalacion, enviarDiagnostico } from "@/lib/instalar";
import { Download, Monitor } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { isIOS, isAndroid, isIOSNoSafari, isStandalone } from "@/lib/push";
import { IlusCompartir, IlusMenu, IlusAndroidMenu } from "@/components/push/Ilustraciones";

const Paso = ({ n, children }) => (
  <li className="flex gap-3 items-start">
    <span className="w-8 h-8 rounded-full bg-[#3A6B56] text-white flex items-center justify-center text-base font-bold shrink-0">{n}</span>
    <div className="text-lg text-[#1C2421] leading-snug pt-0.5">{children}</div>
  </li>
);

const PasosIOS = () => (
  <ol className="space-y-4" data-testid="instalar-pasos-iphone">
    <Paso n={1}>Abre <b>aldiafacil.es</b> en <b>Safari</b>.{isIOSNoSafari() && <p className="text-base text-[#B45309] mt-1">Ahora estás en otro navegador o app: copia la dirección y pégala en Safari.</p>}</Paso>
    <Paso n={2}>Pulsa <b>Compartir</b> (el cuadrado con la flecha hacia arriba).<div className="mt-2"><IlusCompartir /></div></Paso>
    <Paso n={3}>Elige <b>«Añadir a pantalla de inicio»</b> y confirma con «Añadir».<div className="mt-2"><IlusMenu /></div></Paso>
  </ol>
);

const PasosAndroid = () => {
  const [prompt, setPrompt] = useDialogoInstalacion();
  const [nota, setNota] = useState(null);
  const instalar = async () => {
    const r = await lanzarInstalacion();
    setPrompt(false);
    setNota(r === "aceptada" ? "AL DÍA Fácil se está instalando." : r === "error" ? "No se ha podido iniciar la instalación desde aquí. Sigue los pasos de abajo." : null);
  };
  return (
    <div data-testid="instalar-pasos-android">
      {prompt && (
        <button data-testid="instalar-android-btn" onClick={instalar} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg flex items-center justify-center gap-2 mb-4">
          <Download size={20} /> Instalar AL DÍA Fácil
        </button>
      )}
      {nota && <p className="text-base text-[#1C2421] bg-[#E8F0EC] rounded-xl p-3 mb-4" data-testid="instalar-nota-resultado">{nota}</p>}
      <ol className="space-y-4">
        <Paso n={1}>Abre <b>aldiafacil.es</b> en <b>Chrome</b>.</Paso>
        <Paso n={2}>Pulsa los tres puntos <b>⋮</b> y elige <b>«Instalar aplicación»</b> o <b>«Añadir a pantalla de inicio»</b>.<div className="mt-2"><IlusAndroidMenu /></div></Paso>
      </ol>
      <p className="text-base text-[#6E7A74] mt-3">En otros navegadores, busca una opción parecida en su menú.</p>
    </div>
  );
};

const useDialogoInstalacion = () => {
  const [p, setP] = useState(hayDialogoInstalacion);
  useEffect(() => onCambioInstalacion(() => setP(hayDialogoInstalacion())), []);
  return [p, setP];
};

const navegadorPC = () => {
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "edge";
  if (/Chrome\//.test(ua) && !/OPR\/|Brave/.test(ua)) return "chrome";
  if (/Safari\//.test(ua) && /Macintosh/.test(ua) && !/Chrome|Firefox/.test(ua)) return "safari";
  return "otro";
};

const SIN_INSTALAR = "Puedes seguir usando AL DÍA Fácil desde aldiafacil.es y guardar la página en favoritos.";
const PC = {
  chrome: ["Chrome", [<>Abre <b>aldiafacil.es</b> en una ventana normal de Chrome. En ventanas de incógnito o de invitado Chrome no permite instalar.</>, <>Pulsa el icono de <b>instalar</b> que aparece a la derecha de la barra de direcciones, si lo ves.</>, <>O abre el menú <b>⋮</b> (arriba a la derecha) → <b>«Enviar, guardar y compartir»</b> → <b>«Instalar página como aplicación»</b>.</>],
    [<>Si en el menú ves <b>«Abrir AL DÍA»</b> en lugar de «Instalar», puede que ya esté instalada en este ordenador: búscala en el menú Inicio de Windows.</>,
     <>Si no aparece ninguna opción de instalar, puedes crear un <b>acceso directo</b>: menú <b>⋮</b> → <b>«Enviar, guardar y compartir»</b> → <b>«Crear acceso directo»</b>. Es solo un enlace en el escritorio que abre AL DÍA en Chrome; no es una instalación.</>]],
  edge: ["Edge", [<>Abre <b>aldiafacil.es</b> en Edge.</>, <>Abre el menú <b>···</b> y elige <b>Aplicaciones → «Instalar este sitio como aplicación»</b>.</>, <>También puede aparecer un icono de instalar en la barra de direcciones.</>]],
  safari: ["Safari en Mac", [<>Abre <b>aldiafacil.es</b> en Safari.</>, <>En el menú <b>Archivo</b>, elige <b>«Añadir al Dock»</b>.</>, <>Necesita macOS Sonoma o posterior.</>]],
};

export const PasosPC = () => {
  const [nav, setNav] = useState(navegadorPC);
  const [prompt, setPrompt] = useDialogoInstalacion();
  const [nota, setNota] = useState(null);
  const instalar = async () => {
    const r = await lanzarInstalacion();
    enviarDiagnostico("instalar-pasos", r);
    setPrompt(false);
    setNota(r === "aceptada" ? "AL DÍA Fácil se está instalando." : r === "error" ? "No se ha podido iniciar la instalación desde aquí. Sigue los pasos de abajo." : null);
  };
  return (
    <div data-testid="instalar-pasos-ordenador">
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(PC).map(([k, [l]]) => (
          <button key={k} data-testid={`instalar-pc-${k}`} onClick={() => setNav(k)} className={`min-h-[40px] px-3 rounded-full text-sm font-semibold ${nav === k ? "bg-[#1C2421] text-white" : "bg-white border border-[#E5DFD5] text-[#1C2421]"}`}>{l}</button>
        ))}
      </div>
      {prompt && nav !== "safari" && (
        <button data-testid="instalar-pc-btn" onClick={instalar} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg flex items-center justify-center gap-2 mb-4">
          <Download size={20} /> Instalar AL DÍA Fácil
        </button>
      )}
      {nota && <p className="text-base text-[#1C2421] bg-[#E8F0EC] rounded-xl p-3 mb-4" data-testid="instalar-nota-resultado">{nota}</p>}
      {PC[nav] ? (
        <>
          <ol className="space-y-4" data-testid={`instalar-pc-pasos-${nav}`}>{PC[nav][1].map((t, i) => <Paso key={i} n={i + 1}>{t}</Paso>)}</ol>
          {PC[nav][2] ? PC[nav][2].map((t, i) => <p key={i} className="text-base text-[#4A5550] mt-3" data-testid={`instalar-pc-nota-${i}`}>{t}</p>)
            : <p className="text-base text-[#6E7A74] mt-3">Si no aparece la opción: {SIN_INSTALAR.charAt(0).toLowerCase() + SIN_INSTALAR.slice(1)}</p>}
        </>
      ) : <p className="text-lg text-[#1C2421]" data-testid="instalar-pc-sin-soporte">Este navegador no permite instalarla como app. {SIN_INSTALAR}</p>}
    </div>
  );
};

const Pestana = ({ activa, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`flex-1 min-h-[48px] rounded-xl font-semibold text-base transition-colors ${activa ? "bg-[#3A6B56] text-white" : "bg-[#F2EDE4] text-[#1C2421]"}`}>{children}</button>
);

export const InstalarMovil = () => {
  const [abierto, setAbierto] = useState(false);
  const [so, setSo] = useState(isIOS() ? "ios" : isAndroid() ? "android" : "pc");
  if (isStandalone()) return null;
  return (
    <>
      <button data-testid="bienvenida-instalar-movil" onClick={() => setAbierto(true)} className="min-h-[48px] px-4 inline-flex items-center gap-2 text-base font-semibold text-[#4A5550] hover:text-[#3A6B56]">
        <Download size={20} /> Instalar AL DÍA Fácil
      </button>
      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-[#FAF8F5] rounded-2xl" data-testid="instalar-dialogo">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#1C2421] text-left">Instalar AL DÍA Fácil</DialogTitle>
            <DialogDescription className="text-base text-[#4A5550] text-left">Tendrás un acceso directo para abrirla como cualquier otra app.</DialogDescription>
          </DialogHeader>
          <p className="text-base text-[#1C2421] bg-[#E8F0EC] rounded-xl p-3" data-testid="instalar-nota-registro">Si aún no tienes cuenta, primero crea tu cuenta y después instala AL DÍA Fácil.</p>
          <div className="flex gap-2">
            <Pestana testid="instalar-tab-iphone" activa={so === "ios"} onClick={() => setSo("ios")}>iPhone</Pestana>
            <Pestana testid="instalar-tab-android" activa={so === "android"} onClick={() => setSo("android")}>Android</Pestana>
            <Pestana testid="instalar-tab-ordenador" activa={so === "pc"} onClick={() => setSo("pc")}><span className="inline-flex items-center gap-1"><Monitor size={16} /> Ordenador</span></Pestana>
          </div>
          {so === "ios" ? <PasosIOS /> : so === "android" ? <PasosAndroid /> : <PasosPC />}
          <button data-testid="instalar-cerrar" onClick={() => setAbierto(false)} className="w-full min-h-[52px] rounded-xl bg-white border-2 border-[#E5DFD5] text-[#1C2421] font-semibold text-lg">Entendido</button>
        </DialogContent>
      </Dialog>
    </>
  );
};
